import { useState, useRef, useCallback, useEffect, type FormEvent } from "react";
import { Send, Mic, Square } from "lucide-react";
import { cn } from "../lib/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChatInputProps {
  onSend: (text: string) => void;
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Speech recognition type shim (Web Speech API)
// ---------------------------------------------------------------------------

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionInstance;

function getSpeechRecognition(): SpeechRecognitionCtor | null {
  const w = window as unknown as Record<string, unknown>;
  return (w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null) as SpeechRecognitionCtor | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ChatInput({ onSend, disabled = false }: ChatInputProps) {
  const [text, setText] = useState("");
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const canSend = text.trim().length > 0 && !disabled;
  const speechAvailable = typeof window !== "undefined" && !!getSpeechRecognition();

  // Clean up speech recognition on unmount
  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
    };
  }, []);

  const handleSubmit = useCallback(
    (e?: FormEvent) => {
      e?.preventDefault();
      if (!canSend) return;
      onSend(text.trim());
      setText("");
      // Re-focus input after React re-render completes.
      // Using requestAnimationFrame ensures the focus() call runs after
      // the DOM update (the input is never disabled, so focus persists).
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    },
    [canSend, onSend, text]
  );

  const toggleVoice = useCallback(() => {
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    const SpeechRecognition = getSpeechRecognition();
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    // We track a "final transcript" base separately so interim results
    // can replace the display text without losing confirmed words.
    let finalTranscript = "";

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }
      setText(finalTranscript + interim);
    };

    recognition.onerror = () => {
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [isListening]);

  return (
    <form
      onSubmit={handleSubmit}
      className="border-t border-border bg-surface p-3"
    >
      <div className="flex items-center gap-2">
        {/* Text input */}
        <input
          ref={inputRef}
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={isListening ? "Listening..." : "Ask your copilot..."}
          className={cn(
            "h-touch flex-1 rounded-full border border-border bg-surface-inset px-4",
            "text-sm text-text-primary placeholder:text-text-muted",
            "focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20",
            disabled && "opacity-50",
            isListening && "border-error ring-2 ring-error/20"
          )}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSubmit();
            }
          }}
        />

        {/* Voice button */}
        {speechAvailable && (
          <button
            type="button"
            onClick={toggleVoice}
            disabled={disabled}
            data-testid="chat-voice-button"
            data-listening={isListening ? "true" : "false"}
            aria-label={isListening ? "Stop listening" : "Start voice input"}
            className={cn(
              "flex h-touch w-touch items-center justify-center rounded-full",
              "transition-colors",
              isListening
                ? "bg-error text-text-inverse animate-pulse"
                : "bg-surface-inset text-text-secondary active:bg-surface-muted",
              "disabled:opacity-50"
            )}
          >
            {isListening ? (
              <Square className="h-4 w-4" fill="currentColor" />
            ) : (
              <Mic className="h-5 w-5" />
            )}
          </button>
        )}

        {/* Send button */}
        <button
          type="submit"
          disabled={!canSend}
          aria-label="Send message"
          className={cn(
            "flex h-touch w-touch items-center justify-center rounded-full",
            "transition-colors",
            canSend
              ? "bg-brand text-text-inverse active:bg-brand-700"
              : "bg-surface-inset text-text-muted"
          )}
        >
          <Send className="h-5 w-5" />
        </button>
      </div>
    </form>
  );
}
